import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { Chip } from './Chip';
import styles from './TagInput.module.css';

interface TagInputProps {
  readonly value: readonly string[];
  readonly onChange: (value: readonly string[]) => void;
  readonly id?: string;
  readonly describedBy?: string;
  readonly placeholder?: string;
  readonly isInvalid?: boolean;
  readonly disabled?: boolean;
  readonly maxItems?: number;
  readonly maxLength?: number;
  /** Suggestions shown under the field while typing — existing tags, say. */
  readonly suggestions?: readonly string[];
}

const SUGGESTION_LIMIT = 6;

/**
 * Free-text values as removable chips: type, then Enter or a comma. Backspace in an
 * empty field removes the last one. Our control, never a bare input (CLAUDE.md §5).
 * Used for question skills and a short answer's accepted answers (S17).
 */
export function TagInput({ value, onChange, id, describedBy, placeholder, isInvalid = false, disabled = false, maxItems, maxLength, suggestions = [] }: TagInputProps): ReactNode {
  const [draft, setDraft] = useState('');
  const isFull = maxItems !== undefined && value.length >= maxItems;
  const typed = draft.trim().toLowerCase();
  const offered = typed === '' ? [] : suggestions.filter((item) => item.toLowerCase().includes(typed) && !value.includes(item)).slice(0, SUGGESTION_LIMIT);

  function add(text: string): void {
    const next = text.trim();
    setDraft('');
    if (next === '' || isFull || value.some((item) => item.toLowerCase() === next.toLowerCase())) return;
    onChange([...value, next]);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      add(draft);
      return;
    }
    if (event.key === 'Backspace' && draft === '' && value.length > 0) onChange(value.slice(0, -1));
  }

  const classes = [styles.wrap, isInvalid ? styles.invalid : '', disabled ? styles.disabled : ''].filter(Boolean).join(' ');
  return (
    <div className={styles.root}>
      <div className={classes}>
        {value.map((item) => (
          <Chip key={item} tone="neutral" {...(disabled ? {} : { onRemove: () => onChange(value.filter((other) => other !== item)), removeLabel: `Remove ${item}` })}>
            {item}
          </Chip>
        ))}
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={isInvalid || undefined}
          className={styles.input}
          value={draft}
          disabled={disabled || isFull}
          maxLength={maxLength}
          placeholder={isFull ? undefined : placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => add(draft)}
        />
      </div>
      {offered.length > 0 && (
        <div className={styles.suggestions} role="list" aria-label="Suggestions">
          {offered.map((item) => (
            <button key={item} type="button" role="listitem" className={styles.suggestion} onMouseDown={(event) => event.preventDefault()} onClick={() => add(item)}>
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
